// src/components/steps/ProfessionalExperience.jsx
import React, { useState, useEffect, useCallback, memo } from "react";
import CustomSelect from "./AccountSetup/CustomSelect";
import WorkIcon from "../../assets/ProfileSetup/ProfessionalExperience.svg";
import TagInput from "./AccountSetup/TagInput";
import SkipToDashboardButton from "../../components/dashboard/common/SkipToDashboardButton";
import { TrashIcon, PencilIcon } from "@heroicons/react/24/outline";

// --- UPDATED: Reusable Saving Animation Component ---
const SavingAnimation = ({ isSaving, message, subMessage }) => {
  if (!isSaving) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-white bg-opacity-80 backdrop-blur-sm flex flex-col items-center justify-center z-50 transition-opacity duration-300"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="text-center p-8 rounded-lg">
        <svg
          className="animate-spin h-10 w-10 text-emerald-600 mb-4 mx-auto"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <p className="text-lg font-semibold text-gray-800">
          {message || "Saving..."}
        </p>
        <p className="text-sm text-gray-600 mt-1">
          {subMessage || "Please wait a moment."}
        </p>
      </div>
    </div>
  );
};

const initialExperienceState = {
  id: null,
  jobTitle: "",
  companyName: "",
  location: "",
  employmentType: "Full-time",
  startDate: "",
  endDate: "",
  isCurrent: false,
  description: "",
  achievements: [],
  responsibilities: [],
};

const ProfessionalExperience = memo(
  ({
    experiences = [],
    onAddExperience,
    onUpdateExperience,
    onRemoveExperience,
    onNext,
    onBack,
    isSubmitting,
    onSkipToDashboard,
  }) => {
    const [currentExperience, setCurrentExperience] = useState(
      initialExperienceState
    );
    const [editingIndex, setEditingIndex] = useState(null);
    const [formError, setFormError] = useState(null);
    const [isNavigating, setIsNavigating] = useState(false);

    const isLoading = isSubmitting || isNavigating;

    useEffect(() => {
      if (editingIndex !== null && experiences[editingIndex]) {
        const expToEdit = experiences[editingIndex];
        setCurrentExperience({
          id: expToEdit.id,
          jobTitle: expToEdit.title || "",
          companyName: expToEdit.company || "",
          location: expToEdit.location || "",
          employmentType: expToEdit.employment_type || "Full-time",
          startDate: expToEdit.start_date
            ? expToEdit.start_date.substring(0, 10)
            : "",
          endDate:
            !expToEdit.is_current && expToEdit.end_date
              ? expToEdit.end_date.substring(0, 10)
              : "",
          isCurrent: expToEdit.is_current ?? !expToEdit.end_date,
          description: expToEdit.description || "",
          achievements: expToEdit.achievements || [],
          responsibilities: expToEdit.responsibilities || [],
        });
        window.scrollTo(0, 0);
      } else if (editingIndex === null) {
        setCurrentExperience(initialExperienceState);
      }
    }, [editingIndex, experiences]);

    const employmentTypeOptions = [
      { value: "Full-time", label: "Full-time" },
      { value: "Part-time", label: "Part-time" },
      { value: "Contract", label: "Contract" },
      { value: "Freelance", label: "Freelance" },
      { value: "Internship", label: "Internship" },
      { value: "Self-employed", label: "Self-employed" },
    ];

    const handleIsCurrentChange = useCallback((e) => {
      const isChecked = e.target.checked;
      setCurrentExperience((prev) => ({
        ...prev,
        isCurrent: isChecked,
        endDate: isChecked ? "" : prev.endDate,
      }));
    }, []);

    const handleChange = useCallback(
      (e) => {
        const { name, value, type } = e.target;
        if (type === "checkbox" && name === "isCurrent") {
          handleIsCurrentChange(e);
        } else {
          setCurrentExperience((prev) => ({ ...prev, [name]: value }));
        }
        setFormError(null);
      },
      [handleIsCurrentChange]
    );

    const handleAddTag = useCallback(
      (field) => (tag) => {
        if (tag && typeof tag === "string" && tag.trim()) {
          setCurrentExperience((prev) => ({
            ...prev,
            [field]: [...new Set([...(prev[field] || []), tag.trim()])],
          }));
        }
      },
      []
    );

    const handleRemoveTag = useCallback(
      (field) => (index) => {
        setCurrentExperience((prev) => ({
          ...prev,
          [field]: (prev[field] || []).filter((_, i) => i !== index),
        }));
      },
      []
    );

    const handleSaveExperience = async (e) => {
      e.preventDefault();
      setFormError(null);
      if (
        !currentExperience.jobTitle ||
        !currentExperience.companyName ||
        !currentExperience.startDate
      ) {
        setFormError("Job Title, Company Name, and Start Date are required.");
        return;
      }
      if (
        !currentExperience.isCurrent &&
        currentExperience.endDate &&
        currentExperience.startDate &&
        currentExperience.endDate < currentExperience.startDate
      ) {
        setFormError("End Date cannot be earlier than Start Date.");
        return;
      }
      if (!currentExperience.isCurrent && !currentExperience.endDate) {
        setFormError(
          "End Date is required if you are not currently working here."
        );
        return;
      }
      const dataToSend = {
        ...currentExperience,
        endDate: currentExperience.isCurrent
          ? null
          : currentExperience.endDate || null,
      };
      let success = false;
      if (editingIndex !== null) {
        success = await onUpdateExperience(editingIndex, dataToSend);
      } else {
        success = await onAddExperience(dataToSend);
      }
      if (success) {
        setEditingIndex(null);
        setFormError(null);
      }
    };

    const handleEdit = (index) => {
      setEditingIndex(index);
      setFormError(null);
    };

    const handleDelete = async (index) => {
      if (
        window.confirm(
          "Are you sure you want to delete this experience record?"
        )
      ) {
        const success = await onRemoveExperience(index);
        if (success && editingIndex === index) {
          setEditingIndex(null);
        }
      }
    };

    const handleCancelEdit = () => {
      setEditingIndex(null);
      setFormError(null);
    };

    const handleNext = () => {
      setIsNavigating(true);
      setTimeout(() => {
        if (onNext) {
          onNext();
        }
      }, 1000);
    };

    const inputStyle =
      "block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm";
    const primaryButtonStyle =
      "inline-flex items-center justify-center rounded-md border border-transparent bg-emerald-700 px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 disabled:opacity-50";
    const secondaryButtonStyle =
      "rounded-md border border-gray-300 bg-white px-6 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50";
    const formSaveButtonStyle =
      "inline-flex items-center justify-center rounded-md border border-transparent bg-emerald-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 disabled:opacity-50 min-w-[120px]";
    const formCancelButtonStyle =
      "px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1 disabled:opacity-50";

    return (
      <div className="professional-experience p-6 md:p-8">
        <SavingAnimation
          isSaving={isLoading}
          message={
            isNavigating
              ? "Proceeding to next step..."
              : "Saving your experience..."
          }
          subMessage={isNavigating ? "Saving..." : undefined}
        />

        <div className="form-header mb-6 text-center">
          <div className="flex justify-center mb-4">
            <img src={WorkIcon} alt="Work History" className="w-16 h-16" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800">
            Professional Experience
          </h2>
          <p className="text-gray-600 mt-1">
            Add your previous roles and accomplishments.
          </p>
        </div>
        {experiences && experiences.length > 0 && (
          <div className="mb-8 border border-gray-200 rounded-lg p-4 bg-gray-50 shadow-sm">
            <h3 className="text-lg font-semibold mb-3 border-b border-gray-300 pb-2 text-gray-700">
              Your Experience History ({experiences.length})
            </h3>
            <ul className="space-y-3">
              {experiences.map((exp, index) => (
                <li
                  key={exp.id || `exp-${index}`}
                  className="flex flex-col sm:flex-row justify-between sm:items-center p-3 bg-white rounded-md shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-150"
                >
                  <div className="flex-grow mb-2 sm:mb-0 pr-2">
                    <p className="font-medium text-gray-900">
                      {exp.title || "N/A"} at {exp.company || "N/A"}
                    </p>
                    <p className="text-sm text-gray-500">
                      {exp.start_date ? exp.start_date.substring(0, 7) : "N/A"}{" "}
                      -{" "}
                      {exp.is_current
                        ? "Present"
                        : exp.end_date
                        ? exp.end_date.substring(0, 7)
                        : "N/A"}{" "}
                      {exp.location && ` | ${exp.location}`}{" "}
                      {exp.employment_type && ` (${exp.employment_type})`}
                    </p>
                  </div>
                  <div className="flex space-x-2 flex-shrink-0 self-start sm:self-center">
                    <button
                      type="button"
                      onClick={() => handleEdit(index)}
                      disabled={isLoading || editingIndex === index}
                      className="p-1.5 rounded text-blue-600 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                      aria-label="Edit experience"
                      title="Edit"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(index)}
                      disabled={isLoading}
                      className="p-1.5 rounded text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                      aria-label="Delete experience"
                      title="Delete"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        <form
          onSubmit={handleSaveExperience}
          className="border border-gray-200 rounded-lg p-6 shadow-sm bg-white"
        >
          <h3 className="text-lg font-semibold mb-4 text-gray-700 border-b pb-2">
            {editingIndex !== null
              ? "Edit Experience Details"
              : "Add New Experience"}
          </h3>
          {formError && (
            <div className="mb-4 rounded-md bg-red-50 p-3 border border-red-200">
              <p className="text-sm font-medium text-red-800">Input Error</p>
              <p className="mt-1 text-sm text-red-700">{formError}</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mb-4">
            <div>
              <label
                htmlFor={`jobTitle-${editingIndex ?? "add"}`}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Job Title *
              </label>
              <input
                id={`jobTitle-${editingIndex ?? "add"}`}
                type="text"
                name="jobTitle"
                value={currentExperience.jobTitle}
                onChange={handleChange}
                required
                className={inputStyle}
                placeholder="E.g., Software Engineer"
                disabled={isLoading}
              />
            </div>
            <div>
              <label
                htmlFor={`companyName-${editingIndex ?? "add"}`}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Company Name *
              </label>
              <input
                id={`companyName-${editingIndex ?? "add"}`}
                type="text"
                name="companyName"
                value={currentExperience.companyName}
                onChange={handleChange}
                required
                className={inputStyle}
                placeholder="E.g., Tech Solutions Inc."
                disabled={isLoading}
              />
            </div>
          </div>
          <div className="mb-4">
            <label
              htmlFor={`location-${editingIndex ?? "add"}`}
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Location
            </label>
            <input
              id={`location-${editingIndex ?? "add"}`}
              type="text"
              name="location"
              value={currentExperience.location}
              onChange={handleChange}
              className={inputStyle}
              placeholder="E.g., New York, NY or Remote"
              disabled={isLoading}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mb-4">
            <CustomSelect
              label="Employment Type *"
              name="employmentType"
              options={employmentTypeOptions}
              value={currentExperience.employmentType}
              onChange={handleChange}
              required
              idPrefix={`employmentType-${editingIndex ?? "add"}`}
              disabled={isLoading}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration *
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  aria-label="Start Date"
                  type="date"
                  name="startDate"
                  value={currentExperience.startDate}
                  onChange={handleChange}
                  required
                  className={inputStyle + " flex-grow min-w-[150px]"}
                  disabled={isLoading}
                />
                <span className="text-gray-500 mx-1">to</span>
                <input
                  aria-label="End Date"
                  type="date"
                  name="endDate"
                  value={currentExperience.endDate}
                  onChange={handleChange}
                  className={`${inputStyle} flex-grow min-w-[150px]`}
                  min={currentExperience.startDate || undefined}
                  disabled={isLoading || currentExperience.isCurrent}
                  required={!currentExperience.isCurrent}
                />
              </div>
              <div className="mt-2 flex items-center">
                <input
                  id={`isCurrent-${editingIndex ?? "add"}`}
                  name="isCurrent"
                  type="checkbox"
                  checked={currentExperience.isCurrent}
                  onChange={handleChange}
                  className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  disabled={isLoading}
                />
                <label
                  htmlFor={`isCurrent-${editingIndex ?? "add"}`}
                  className="ml-2 block text-sm text-gray-900"
                >
                  I currently work here
                </label>
              </div>
            </div>
          </div>
          <div className="mb-4">
            <label
              htmlFor={`description-${editingIndex ?? "add"}`}
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Description
            </label>
            <textarea
              id={`description-${editingIndex ?? "add"}`}
              name="description"
              value={currentExperience.description}
              onChange={handleChange}
              rows="3"
              className={inputStyle}
              placeholder="Optionally describe your role..."
              disabled={isLoading}
            ></textarea>
          </div>
          <div className="mb-4">
            <TagInput
              label="Key Achievements (Optional)"
              tags={currentExperience.achievements}
              onAddTag={handleAddTag("achievements")}
              onRemoveTag={handleRemoveTag("achievements")}
              placeholder="Press Enter after each achievement"
              inputId={`achievements-${editingIndex ?? "add"}`}
              disabled={isLoading}
            />
          </div>
          <div className="mb-4">
            <TagInput
              label="Key Responsibilities (Optional)"
              tags={currentExperience.responsibilities}
              onAddTag={handleAddTag("responsibilities")}
              onRemoveTag={handleRemoveTag("responsibilities")}
              placeholder="Press Enter after each responsibility"
              inputId={`responsibilities-${editingIndex ?? "add"}`}
              disabled={isLoading}
            />
          </div>
          <div className="flex justify-end gap-3 mt-6 border-t border-gray-200 pt-4">
            {editingIndex !== null && (
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={isLoading}
                className={formCancelButtonStyle}
              >
                Cancel Edit
              </button>
            )}
            <button
              type="submit"
              disabled={isLoading}
              className={`${formSaveButtonStyle} ${
                isLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isSubmitting
                ? "Saving..."
                : editingIndex !== null
                ? "Save Changes"
                : "Add Experience"}
            </button>
          </div>
        </form>

        {/* Navigation Buttons */}
        <div className="flex flex-col sm:flex-row justify-between items-center pt-8 gap-4 border-t border-gray-200 mt-10">
          <div className="flex flex-col sm:flex-row gap-x-4 gap-y-4 w-full sm:w-auto order-last sm:order-first">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                disabled={isLoading}
                className={`${secondaryButtonStyle} w-full sm:w-auto`}
              >
                Back
              </button>
            )}
            {onSkipToDashboard && (
              <SkipToDashboardButton
                onSkip={onSkipToDashboard}
                disabled={isLoading}
                className="w-full sm:w-auto"
              />
            )}
          </div>

          <button
            type="button"
            onClick={handleNext}
            disabled={isLoading}
            className={`${primaryButtonStyle} w-full sm:w-auto order-first sm:order-last`}
          >
            Next Step
          </button>
        </div>
      </div>
    );
  }
);

export default ProfessionalExperience;
